import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { User, Search, ArrowLeft, Users, Phone, Mail, Calendar, Edit2, Trash2, ChevronRight, X, LayoutGrid, ShoppingBag, IndianRupee, Clock, Hash, CheckCircle } from "lucide-react";
import authAxios from "../../api/authAxios";
import { NotificationContext } from "../../context/NotificationContext";
import { motion, AnimatePresence } from "framer-motion";

export default function OwnerCustomerReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [owner, setOwner] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCust, setSelectedCust] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [custInvoices, setCustInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [res, userRes] = await Promise.all([
        authAxios.get(`/api/super-admin/owners/${id}/owner_details/`),
        authAxios.get(`/api/super-admin/owners/${id}/`)
      ]);
      setCustomers(res.data.customers || []);
      setOwner(userRes.data);
    } catch (error) {
      addNotification("Error loading customer data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (cust) => {
    setSelectedCust(cust);
    setShowDetailModal(true);
    setLoadingInvoices(true);
    try {
        // Fetch specific invoices for this customer
        const res = await authAxios.get(`/api/super-admin/owners/${id}/owner_details/`);
        // Filter invoices for this customer locally for now, or use a dedicated endpoint if exists
        // Since we are super admin, we filter the owner's invoices by customer name/id
        const filtered = res.data.invoices.filter(inv => 
            inv.customer == cust.id || 
            inv.customer_name?.toLowerCase().trim() === cust.name?.toLowerCase().trim()
        );
        setCustInvoices(filtered);
    } catch (err) {
        addNotification("Error loading purchase history", "error");
    } finally {
        setLoadingInvoices(false);
    }
  };

  const filteredCustomers = customers.filter(cust => 
    cust.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    cust.phone?.includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-bold text-sm uppercase tracking-wider">Back to User Management</span>
        </button>

        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-xl border border-slate-200 dark:border-slate-700 mb-8 relative overflow-hidden">
            <div className="absolute top-[-20px] right-[-20px] opacity-10">
                <Users className="w-64 h-64 text-indigo-500" />
            </div>
            
            <div className="relative z-10">
                <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Customer Base Report</h1>
                {owner && (
                    <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-full text-xs font-black uppercase tracking-widest border border-indigo-500/20">
                            {owner.last_name || owner.business_name}
                        </span>
                        <div className="flex items-center gap-4 text-slate-400 text-sm font-bold ml-2">
                             <div className="flex items-center gap-1"><Users className="w-4 h-4" /> {customers.length} Total Registered</div>
                        </div>
                    </div>
                )}
            </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-6 border-b border-slate-100 dark:border-slate-700">
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                        type="text"
                        placeholder="Search by name or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-bold"
                    />
                </div>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {loading ? (
                    <div className="col-span-full py-20 flex flex-col items-center justify-center gap-4">
                        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Querying Customer Database...</p>
                    </div>
                ) : filteredCustomers.length === 0 ? (
                    <div className="col-span-full py-20 text-center text-slate-400 font-black uppercase text-xs">No matching customers found</div>
                ) : filteredCustomers.map(cust => (
                    <div key={cust.id} className="bg-white dark:bg-slate-800 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-700 shadow-xl shadow-slate-200/50 dark:shadow-none relative group transition-all hover:scale-[1.01]">
                        {/* Status & Actions */}
                        <div className="flex justify-between items-start mb-6">
                            <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600">
                                <User className="w-8 h-8" />
                            </div>
                            <div className="flex gap-2">
                                <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors">
                                    <Edit2 className="w-4 h-4 text-slate-400" />
                                </button>
                                <button className="p-2 hover:bg-red-50 text-red-500 rounded-xl transition-colors">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Profile Info */}
                        <div className="mb-6">
                            <h3 className="text-xl font-black text-slate-900 dark:text-white mb-1">{cust.name}</h3>
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{cust.customer_id}</p>
                        </div>

                        {/* Stats Cards */}
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            <div className="bg-slate-50 dark:bg-slate-900/40 p-4 rounded-3xl border border-dotted border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-2 text-slate-400 mb-1">
                                    <ShoppingBag className="w-3 h-3" />
                                    <span className="text-[10px] font-black uppercase tracking-widest">Total Orders</span>
                                </div>
                                <p className="text-2xl font-black text-slate-900 dark:text-white">{cust.total_orders || 0}</p>
                            </div>
                            <div className="bg-slate-50 dark:bg-slate-900/40 p-4 rounded-3xl border border-dotted border-slate-200 dark:border-slate-700">
                                <div className="flex items-center gap-2 text-slate-400 mb-1">
                                    <IndianRupee className="w-3 h-3" />
                                    <span className="text-[10px] font-black uppercase tracking-widest">Total Spent</span>
                                </div>
                                <p className="text-xl font-black text-emerald-600">₹{(cust.total_spent || 0).toLocaleString()}</p>
                            </div>
                        </div>

                        {/* Contact & Date */}
                        <div className="space-y-3 mb-8">
                            <div className="flex items-center gap-3 text-slate-600 dark:text-slate-400">
                                <Phone className="w-4 h-4 text-slate-400" />
                                <span className="text-sm font-bold font-mono">{cust.phone}</span>
                                <span className="ml-auto px-2 py-0.5 bg-emerald-100 text-emerald-600 rounded text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" /> Active
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
                                <Clock className="w-4 h-4 text-slate-400" />
                                <span className="text-xs font-bold uppercase truncate tracking-wider">Last Visit: {new Date(cust.created_at).toLocaleDateString()}</span>
                            </div>
                        </div>

                        {/* View Details Button */}
                        <button 
                            onClick={() => handleViewDetails(cust)}
                            className="w-full py-4 bg-blue-50 dark:bg-blue-900/10 text-blue-600 hover:bg-blue-600 hover:text-white rounded-2xl font-black uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-2 group/btn"
                        >
                            View Details
                            <ChevronRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
      </div>

      {/* DETAILED MODAL (Matching Image 2) */}
      <AnimatePresence>
        {showDetailModal && selectedCust && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDetailModal(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative bg-white dark:bg-slate-800 w-full max-w-5xl rounded-[3rem] shadow-2xl overflow-hidden flex flex-col md:flex-row h-[85vh] max-h-[800px]"
            >
              {/* Sidebar Info */}
              <div className="w-full md:w-80 bg-slate-50 dark:bg-slate-900/50 p-8 border-r border-slate-100 dark:border-slate-700 flex flex-col items-center">
                <div className="w-32 h-32 rounded-[2rem] bg-gradient-to-br from-blue-500 to-indigo-600 shadow-xl shadow-blue-500/20 flex items-center justify-center text-white text-4xl font-black mb-6">
                    {selectedCust.name.charAt(0)}
                </div>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">{selectedCust.name}</h2>
                <span className="px-3 py-1 bg-blue-500/10 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-widest border border-blue-500/20 mb-8">
                    {selectedCust.customer_id}
                </span>

                <div className="w-full space-y-4">
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-3xl border border-slate-100 dark:border-slate-700">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 px-1">Phone Number</p>
                        <div className="flex items-center gap-3 text-slate-900 dark:text-white">
                            <Phone className="w-4 h-4 text-blue-500" />
                            <span className="font-mono font-bold tracking-tighter text-lg">{selectedCust.phone}</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-4 rounded-3xl border border-slate-100 dark:border-slate-700">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 px-1">Joined Date</p>
                        <div className="flex items-center gap-3 text-slate-900 dark:text-white font-bold">
                            <Calendar className="w-4 h-4 text-blue-500" />
                            <span className="text-sm">{new Date(selectedCust.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                    </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="flex-1 p-8 overflow-y-auto">
                <div className="flex justify-between items-center mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-900 flex items-center justify-center text-blue-600">
                            <LayoutGrid className="w-5 h-5" />
                        </div>
                        <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Purchase Dashboard</h2>
                    </div>
                    <button 
                        onClick={() => setShowDetailModal(false)}
                        className="p-3 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-2xl transition-colors"
                    >
                        <X className="w-6 h-6 text-slate-400" />
                    </button>
                </div>

                {/* Hero Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
                    <div className="bg-blue-600 p-8 rounded-[2rem] text-white shadow-xl shadow-blue-600/30 relative overflow-hidden">
                        <div className="absolute top-[-10px] right-[-10px] opacity-10">
                            <ShoppingBag className="w-32 h-32" />
                        </div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-2 mb-4 opacity-80">
                                <ShoppingBag className="w-4 h-4" />
                                <span className="text-xs font-black uppercase tracking-[0.2em]">Total Orders</span>
                            </div>
                            <p className="text-5xl font-black">{selectedCust.total_orders || 0}</p>
                        </div>
                    </div>

                    <motion.div 
                        whileHover={{ scale: 1.02 }}
                        className="bg-emerald-500 p-8 rounded-[2rem] text-white shadow-xl shadow-emerald-500/30 relative overflow-hidden"
                    >
                        <div className="absolute top-[-10px] right-[-10px] opacity-10">
                            <IndianRupee className="w-32 h-32" />
                        </div>
                        <div className="relative z-10">
                            <div className="flex items-center gap-2 mb-4 opacity-80">
                                <IndianRupee className="w-4 h-4" />
                                <span className="text-xs font-black uppercase tracking-[0.2em]">Total Spend</span>
                            </div>
                            <p className="text-4xl font-black">₹{(selectedCust.total_spent || 0).toLocaleString()}</p>
                        </div>
                    </motion.div>
                </div>

                {/* Invoice History */}
                <div>
                    <div className="flex items-center gap-3 mb-6">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">Recent Invoice History</h3>
                    </div>

                    <div className="space-y-4">
                        {loadingInvoices ? (
                            <div className="py-12 flex justify-center">
                                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : custInvoices.length === 0 ? (
                            <div className="py-12 bg-slate-50 dark:bg-slate-900/30 rounded-3xl text-center border-2 border-dashed border-slate-200 dark:border-slate-700">
                                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">No detailed ledger records found</p>
                            </div>
                        ) : custInvoices.map((inv, idx) => (
                            <motion.div 
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.05 }}
                                key={inv.id} 
                                className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-700 p-5 rounded-[1.5rem] flex items-center justify-between shadow-sm hover:shadow-md transition-shadow group"
                            >
                                <div className="flex items-center gap-5">
                                    <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-slate-800 flex items-center justify-center text-blue-600 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                                        <Hash className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="font-black text-slate-900 dark:text-white font-mono">{inv.invoice_number}</p>
                                        <p className="text-xs text-slate-400 font-bold">{new Date(inv.invoice_date).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-xl font-black text-slate-900 dark:text-white">₹{parseFloat(inv.total_amount).toLocaleString()}</p>
                                    <p className="text-[10px] font-black uppercase text-emerald-600 tracking-widest">Completed</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
